import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { ASSETS } from "../../config/assets";
import { useAuth } from "../../context/AuthContext";
import { useLoading } from "../../context/LoadingContext";
// Bublinky se renderují stejnými třídami jako v uživatelském chatu
import "../../components/chat/ChatModal.css";
import "./AdminConversations.css";
import "./AdminDashboard.css";

const SUBJECT_LABELS = {
  item: "Reakce na inzerát",
  user: "Reakce na profil",
};

// Čas v zprávě se zobrazuje u každé zprávy – moderátor potřebuje vidět,
// kdy přesně byla odeslána, ne jen kdy začala konverzace.
const formatDateTime = (value) =>
  value
    ? new Intl.DateTimeFormat("cs-CZ", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value))
    : "Neznámé";

const formatShort = (value) =>
  value
    ? new Intl.DateTimeFormat("cs-CZ", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value))
    : "—";

/**
 * Jeden řádek zprávy. Reaguje se na stejné CSS třídy jako uživatelský
 * chat (ChatModal.css), jen bez tlačítka na odeslání – moderátor
 * do konverzace nezasahuje, jen si ji přečte.
 */
const AdminMessageRow = ({
  message,
  perspectiveId,
  participants,
  reaction,
}) => {
  const attachments = message.attachments ?? [];
  // Reakce přilepená k této zprávě; pokud je v řádku sama reakce
  // (její zpráva v seznamu chybí), použijeme její vlastní kontext
  const reactionInfo =
    reaction?.context ?? (message.is_system ? message.context : null);
  // Reakce je systémová zpráva – payload jí posílá odesílatele, ale
  // jako zprávu ji neplatí. Bez toho se u ní ukazovalo „Neznámý“.
  const participant =
    message.sender ??
    participants.find((user) => user.id === message.user_id) ??
    null;
  // Moderátor musí vidět, kdo zprávu poslal a kdy – bez času se nahlášení
  // na konkrétní okamžik nedá vyřešit.
  const author = participant
    ? `${participant.first_name} ${participant.last_name}`
    : "Neznámý";
  const isCounterpart = message.user_id !== perspectiveId;

  return (
    <div className={`chat_row ${isCounterpart ? "other" : "own"}`}>
      {/* Avatar patří i ke smazané zprávě – moderátor musí vidět, o čí
          hlášku jde, a to i když autor text smazal */}
      {isCounterpart ? (
        <img
          className="chat_row_avatar"
          src={participant?.profile_image_url || ASSETS.default_avatar}
          alt=""
        />
      ) : null}

      <div className="chat_content">
        <div className="chat_bubble_wrap">
          <div className="admin-chat-message-meta">
            {/* Jméno autora je odkaz na profil – stejně jako v záhlaví
                konverzace, aby šlo profil otevřít bez zavření administrace */}
            <a
              className="admin-chat-author"
              href={`/user/${message.user_id}/profile`}
              target="_blank"
              rel="noreferrer"
            >
              {author}
            </a>
            <span className="admin-chat-message-id">#{message.user_id}</span>
            <span>{formatDateTime(message.created_at)}</span>
          </div>
          {/* Reakce patří k této zprávě – stojí nad textem jako v chatu.
              Její vlastní text je jen kopie názvu odkazovaného předmětu,
              proto se vykreslí jednou, tady. */}
          {reactionInfo && (
            <a
              className={`chat_reaction_link${message.is_deleted ? " admin-chat_reaction_gone" : ""
                }`}
              href={reactionInfo.url}
              target="_blank"
              rel="noreferrer"
              title={reactionInfo.title}
            >
              <div className="chat_reaction">
                <span className="chat_reaction_label">
                  📌 {SUBJECT_LABELS[reactionInfo.subject_type] ?? "Reakce"}:
                </span>{" "}
                <span className="chat_reaction_title">
                  {reactionInfo.title}
                </span>
              </div>
            </a>
          )}

          {attachments.length > 0 && (
            /* Fotky smazané zprávy se v chatu uživateli skryjí, ale
               moderátor je musí vidět – nahlášení se posuzuje i podle nich. */
            <div
              className={`chat_bubble chat_bubble_photos${message.is_deleted ? " admin-chat_photos_deleted" : ""
                }`}
            >
              <div
                className={`chat_attachments chat_attachments_${attachments.length > 1 ? "grid" : "single"}`}
              >
                {attachments.map((attachment) => (
                  <a
                    key={attachment.id ?? attachment.url}
                    className="chat_photo"
                    href={attachment.url}
                    target="_blank"
                    rel="noreferrer"
                    title={attachment.name}
                  >
                    <img
                      src={attachment.url}
                      alt={attachment.name}
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {message.is_deleted ? (
            /* Text smazané zprávy je důkaz – moderátor ho musí vidět,
               jinak se nahlášení na konkrétní výrok nedá posoudit. */
            <div className="chat_bubble chat_bubble_deleted admin-chat_deleted">
              {message.body && (
                <p className="chat_bubble_text admin-chat_deleted_body">
                  {message.body}
                </p>
              )}
              <p className="chat_bubble_text body_smallest strong admin-chat_deleted_note">
                <i className="fa-solid fa-eraser"></i> Smazal(a) {author}{" "}
                {formatDateTime(message.deleted_at)}
              </p>
            </div>
          ) : (
            message.body && (
              <div className="chat_bubble">
                <p className="chat_bubble_text">{message.body}</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Prohlížení konverzací pro moderaci.
 *
 * Na rozdíl od uživatelského chatu je čistě čtecí – žádné realtime
 * připojení, psaní ani mazání. Moderátor sem chodí řešit nahlášení
 * na chat, ne komunikovat za uživatele.
 */
const AdminConversations = () => {
  const { user } = useAuth();
  const { setLoading } = useLoading();
  const [searchParams, setSearchParams] = useSearchParams();

  const [conversations, setConversations] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [activeId, setActiveId] = useState(
    searchParams.get("conversation")
      ? Number(searchParams.get("conversation"))
      : null,
  );
  const [detail, setDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const canModerate = ["admin_moderator", "super_admin"].includes(user?.role);

  const fetchConversations = useCallback(async (page = 1, term = "") => {
    setLoadingList(true);

    try {
      const { data } = await axiosInstance.get("/api/admin/conversations", {
        params: { page, search: term || undefined },
      });

      setConversations(data.data ?? []);
      setPagination(data);
    } finally {
      setLoadingList(false);
    }
  }, []);

  /* Scroll jako v uživatelském chatu: konverzace se otevírá u nejnovější
     zprávy, ne na začátku historie. Fotky se dočítají až po vykreslení,
     proto konec držíme i po nich – dokud uživatel neodscrolluje. */
  const readerRef = useRef(null);
  const isAtBottomRef = useRef(true);

  const isScrolledToBottom = () => {
    const el = readerRef.current;

    if (!el) return true;

    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const scrollToBottom = useCallback(() => {
    const el = readerRef.current;

    if (!el) return;

    // "instant" obejde globálně nastavené scroll-behavior: smooth
    if (typeof el.scrollTo === "function") {
      el.scrollTo({ top: el.scrollHeight, behavior: "instant" });

      return;
    }

    el.scrollTop = el.scrollHeight;
  }, []);

  // Otevření jiné konverzace musí skočit na její konec, ne zůstat
  // na pozici z předchozí
  useLayoutEffect(() => {
    if (!detail) return;

    isAtBottomRef.current = true;
    scrollToBottom();
  }, [detail, scrollToBottom]);

  // Fotky a dlouhé texty mění výšku obsahu až po vykreslení
  useEffect(() => {
    const el = readerRef.current;

    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(() => {
      if (!isAtBottomRef.current) return;

      scrollToBottom();
    });

    observer.observe(el);

    for (const child of el.children) observer.observe(child);

    return () => observer.disconnect();
  }, [detail, scrollToBottom]);

  const fetchDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null);

      return;
    }

    setLoadingDetail(true);

    try {
      const { data } = await axiosInstance.get(
        `/api/admin/conversations/${id}`,
      );

      setDetail(data);
    } catch {
      setDetail(null);
      setActiveId(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // Bez oprávnění se nesmí ani jeden požadavek odpálit – kontrola je
  // až v JSX, effekt by se spustil dřív a data by si tahal každý,
  // kdo na adresu přijde.
  useEffect(() => {
    if (!canModerate) return;

    fetchConversations(1, search);
  }, [canModerate, fetchConversations, search]);

  useEffect(() => {
    if (!canModerate) return;

    fetchDetail(activeId);
  }, [canModerate, activeId, fetchDetail]);

  // Načítání obrázků a databáze není okamžité – stránka nechá hlavní
  // loader, ať uživatel vidí, že se něco děje
  useEffect(() => {
    setLoading(loadingList || loadingDetail);
  }, [loadingList, loadingDetail, setLoading]);

  const selectConversation = (id) => {
    setActiveId(id);

    const next = new URLSearchParams(searchParams);
    next.set("conversation", String(id));
    setSearchParams(next, { replace: true });
  };

  if (!canModerate) {
    return (
      <div className="admin-container">
        <div className="admin-content">
          <p>Nemáte oprávnění prohlížet konverzace.</p>
        </div>
      </div>
    );
  }

  // Konverzace se zobrazuje z pohledu toho, kdo ji nahlásil: jeho zprávy
  // vpravo jako v běžném chatu, zprávy druhého vlevo. Bez reporterId
  // v URL ( moderátor otevřel konverzaci přímo ze seznamu) bereme
  // prvního účastníka.
  const participants = detail ? detail.conversation.participants : [];
  const reporterId = Number(searchParams.get("reporter")) || null;
  const perspectiveId =
    reporterId ??
    participants.find((user) => user.id !== reporterId)?.id ??
    participants[0]?.id ??
    null;
  // Účastníci v pořadí, v jakém jsou v chatu: vlevo ten, jehož zprávy
  // leží na levé straně, vpravo ten, z jehož pohledu se čte.
  const leftParticipant =
    participants.find((user) => user.id !== perspectiveId) ?? participants[0];

  const orderedParticipants = leftParticipant
    ? [
      leftParticipant,
      ...participants.filter((user) => user.id !== leftParticipant.id),
    ]
    : participants;

  const perspectiveUser = participants.find(
    (user) => user.id === perspectiveId,
  );
  /* Systémová hláška „📌 Reakce na …“ se v chatu vykresluje nad zprávou,
     na kterou odkazuje, ne jako samostatná zpráva. V administraci to
     bez seskupení znamenalo dva řádky navíc a zbytečnou kopii textu.
     Reakce bez existující zprávy si musí vlastní řádek ponechat, jinak
     bychom o ní věděli jen to, že je mimo seznam. */
  const allMessages = detail?.messages ?? [];
  const knownIds = new Set(allMessages.map((message) => message.id));
  const reactionByParent = new Map();

  allMessages.forEach((message) => {
    if (message.is_system && message.context_for_message_id) {
      reactionByParent.set(message.context_for_message_id, message);
    }
  });

  const isAttachedReaction = (message) =>
    message.is_system &&
    message.context_for_message_id &&
    knownIds.has(message.context_for_message_id);

  const listedMessages = allMessages.filter(
    (message) => !isAttachedReaction(message),
  );

  return (
    <div className="admin-container">
      <div className="admin-content admin-chat-layout">
        <section
          className="admin-chat-reader"
          ref={readerRef}
          onScroll={() => {
            isAtBottomRef.current = isScrolledToBottom();
          }}
        >
          {!activeId && (
            <p className="admin-chat-hint">
              Vyberte konverzaci ze seznamu vlevo.
            </p>
          )}

          {loadingDetail && <p className="admin-chat-hint">Načítám zprávy…</p>}

          {detail && (
            <>
              {/* Záhlaví drží při čtení nahoře – konverzace je dlouhá
                  a bez toho by se po scrollu ztratilo, koho vlastně čtu. */}
              <div className="admin-chat-top">
                <h2 className="admin-chat-heading">
                  Konverzace s {leftParticipant?.first_name}{" "}
                  {leftParticipant?.last_name}
                </h2>
                {detail.conversation.deleted_at && (
                  <p className="admin-chat-deleted">
                    <i className="fa-solid fa-trash"></i> SMAZÁNO
                  </p>
                )}
                <p className="admin-chat-note">
                  <i className="fa-solid fa-eye"></i> Jen pro čtení –
                  moderátor do konverzace nezasahuje a uživatelé nevidí, že
                  si ji prohlíží.
                </p>
                {reporterId && perspectiveUser && (
                  <p className="admin-chat-perspective">
                    Pohled uživatele:{" "}
                    <strong>
                      {perspectiveUser.first_name} {perspectiveUser.last_name}
                    </strong>{" "}
                    (nahlásil/a)
                  </p>
                )}
                <p className="admin-chat-counts">
                  <strong>{detail.conversation.message_count}</strong> zpráv celkem
                  {detail.conversation.deleted_message_count > 0
                    ? ` · ${detail.conversation.deleted_message_count} smazaných`
                    : ""}
                </p>
                <div className="admin-chat-participants">
                  {orderedParticipants.map((participant) => (
                    <a
                      key={participant.id}
                      className={`admin-chat-participant${participant.id === leftParticipant?.id
                          ? " left"
                          : " right"
                        }`}
                      href={`/user/${participant.id}/profile`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src={
                          participant.profile_image_url || ASSETS.default_avatar
                        }
                        alt=""
                      />
                      <span>
                        {participant.first_name} {participant.last_name}
                      </span>
                      <small>#{participant.id}</small>
                    </a>
                  ))}
                </div>
              </div>

              <div className="admin-chat-messages">
                {listedMessages.map((message) => (
                  <AdminMessageRow
                    key={message.id}
                    message={message}
                    perspectiveId={perspectiveId}
                    participants={participants}
                    reaction={reactionByParent.get(message.id) ?? null}
                  />
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminConversations;
